import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Pencil, Trash2, Globe, FileEdit, Loader2, RefreshCw } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ArticleEditor, { DeleteConfirm } from "@/components/admin/ArticleEditor";
import EditorialCalendar from "@/components/admin/EditorialCalendar";
import { CITIES } from "@/data/content";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genOpen, setGenOpen] = useState(false);
  const [genCity, setGenCity] = useState("lagny-sur-marne");
  const [genTopic, setGenTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [refreshing, setRefreshing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data);
    } catch (e) {
      toast.error("Impossible de charger les articles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(
        "/admin/blog/generate",
        { city_slug: genCity || null, topic: genTopic.trim() || null },
        { timeout: 120000 }
      );
      toast.success(`Brouillon généré : « ${data.title} ». Relisez-le avant publication.`);
      setGenOpen(false);
      setGenTopic("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const setStatus = async (post, status) => {
    try {
      await api.patch(`/admin/blog/${post.id}`, { status });
      toast.success(status === "publié" ? "Article publié en ligne." : "Article repassé en brouillon.");
      load();
    } catch (e) {
      toast.error("Échec de la mise à jour.");
    }
  };

  const refreshArticle = async (post) => {
    setRefreshing(post.id);
    try {
      await api.post(`/admin/blog/${post.id}/refresh`, null, { timeout: 120000 });
      toast.success(`Version enrichie créée en brouillon pour « ${post.title} ».`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRefreshing(null);
    }
  };

  const drafts = posts.filter((p) => p.status !== "publié").length;

  return (
    <div data-testid="admin-blog">
      <EditorialCalendar />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display font-semibold text-2xl text-brand-navy">
            Articles SEO
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {posts.length} article{posts.length > 1 ? "s" : ""} — {drafts} brouillon
            {drafts > 1 ? "s" : ""} en attente de validation. 1 nouvel article par semaine +
            enrichissement automatique des articles de plus de 6 mois.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={load}
            data-testid="refresh-articles-btn"
            aria-label="Rafraîchir"
          >
            <RefreshCw className={"w-4 h-4 " + (loading ? "animate-spin" : "")} />
          </Button>
          <Button
            onClick={() => setGenOpen(true)}
            data-testid="generate-article-btn"
            className="bg-brand-gold hover:bg-brand-golddark text-brand-navy font-semibold"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Générer un article IA
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-brand-line bg-white overflow-hidden shadow-sm">
        {posts.length === 0 && !loading ? (
          <div className="py-20 text-center text-slate-400" data-testid="articles-empty">
            Aucun article. Générez votre premier brouillon IA.
          </div>
        ) : (
          <Table data-testid="articles-table">
            <TableHeader>
              <TableRow className="border-brand-line hover:bg-transparent">
                <TableHead className="text-slate-500">Titre</TableHead>
                <TableHead className="text-slate-500">Ville ciblée</TableHead>
                <TableHead className="text-slate-500">Statut</TableHead>
                <TableHead className="text-slate-500">Origine</TableHead>
                <TableHead className="text-slate-500">Date</TableHead>
                <TableHead className="text-slate-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {posts.map((p) => (
                <TableRow
                  key={p.id}
                  data-testid={`article-row-${p.id}`}
                  className="border-brand-line hover:bg-brand-mist/60"
                >
                  <TableCell className="font-medium text-brand-navy max-w-[320px]">
                    <span className="line-clamp-2">{p.title}</span>
                    <span className="text-xs text-slate-400">/blog/{p.slug}</span>
                    {p.refresh_of && (
                      <Badge
                        data-testid={`maj-badge-${p.id}`}
                        className="ml-2 rounded-full border-0 bg-amber-100 text-amber-700 text-[10px]"
                      >
                        MAJ
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-700">{p.city_name || "—"}</TableCell>
                  <TableCell>
                    <Badge
                      data-testid={`status-${p.id}`}
                      className={
                        "rounded-full border-0 " +
                        (p.status === "publié"
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-brand-gold text-brand-navy")
                      }
                    >
                      {p.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs uppercase tracking-wider text-slate-500">
                      {p.source === "ia" ? "IA" : "Manuel"}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">{p.dateLabel}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditing(p)}
                        data-testid={`edit-${p.id}`}
                        className="p-2 rounded-lg text-slate-500 hover:text-brand-navy hover:bg-brand-mist transition-colors duration-300"
                        aria-label="Modifier"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {!p.refresh_of && (
                        <button
                          onClick={() => refreshArticle(p)}
                          disabled={refreshing === p.id}
                          data-testid={`refresh-${p.id}`}
                          className="p-2 rounded-lg text-slate-500 hover:text-brand-golddark hover:bg-amber-50 transition-colors duration-300 disabled:opacity-50"
                          aria-label="Réécrire avec l'IA"
                          title="Réécrire / enrichir avec l'IA (crée un brouillon)"
                        >
                          {refreshing === p.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4" />
                          )}
                        </button>
                      )}
                      {p.status === "publié" ? (
                        <button
                          onClick={() => setStatus(p, "brouillon")}
                          data-testid={`unpublish-${p.id}`}
                          className="p-2 rounded-lg text-slate-500 hover:text-amber-600 hover:bg-amber-50 transition-colors duration-300"
                          aria-label="Dépublier"
                          title="Dépublier"
                        >
                          <FileEdit className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => setStatus(p, "publié")}
                          data-testid={`publish-${p.id}`}
                          className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors duration-300"
                          aria-label="Publier"
                          title="Publier"
                        >
                          <Globe className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleting(p)}
                        data-testid={`delete-article-${p.id}`}
                        className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors duration-300"
                        aria-label="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={genOpen} onOpenChange={(o) => !generating && setGenOpen(o)}>
        <DialogContent className="max-w-md bg-white" data-testid="generate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-brand-navy flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand-gold" /> Générer un article SEO
            </DialogTitle>
            <DialogDescription>
              L&apos;IA rédige un brouillon localisé et factuel. Rien n&apos;est publié sans
              votre validation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium block mb-1">
                Ville ciblée
              </label>
              <Select value={genCity} onValueChange={setGenCity}>
                <SelectTrigger data-testid="generate-city-select">
                  <SelectValue placeholder="Choisir une ville" />
                </SelectTrigger>
                <SelectContent>
                  {CITIES.map((c) => (
                    <SelectItem key={c.slug} value={c.slug} data-testid={`city-option-${c.slug}`}>
                      {c.name} ({c.cp})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium block mb-1">
                Sujet (optionnel)
              </label>
              <Input
                data-testid="generate-topic-input"
                value={genTopic}
                onChange={(e) => setGenTopic(e.target.value)}
                placeholder="Ex : clé cassée dans la serrure"
              />
              <p className="text-xs text-slate-400 mt-1">
                Laissez vide pour une suggestion automatique non dupliquée.
              </p>
            </div>
            <Button
              onClick={generate}
              disabled={generating}
              data-testid="generate-submit-btn"
              className="w-full bg-brand-navy hover:bg-brand-navy2 text-white"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rédaction en cours (30-60 s)…
                </>
              ) : (
                "Générer le brouillon"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ArticleEditor post={editing} onClose={() => setEditing(null)} onSaved={load} />
      <DeleteConfirm post={deleting} onClose={() => setDeleting(null)} onDeleted={load} />
    </div>
  );
}
